import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, Pencil, Send, Check, X, Trash2, Paperclip } from 'lucide-react';
import { ApprovalBadge, isApprover, apiError } from '@/components/sales/ApprovalBits';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const Row = ({ k, v }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-slate-500">{k}</div><div className="text-sm font-medium text-slate-900">{v || '—'}</div></div>
);

const download = async (API, order, att) => {
  try {
    const res = await axios.get(`${API}/sales-orders/${order.order_id}/attachments/${att.attachment_id}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = att.filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error('Download failed'); }
};

export const SalesOrderView = ({ order, onClose, user, API, onEdit, onSubmit, onApprove, onReject, onChanged }) => {
  if (!order) return null;
  const editable = ['draft', 'rejected'].includes(order.status) && (order.created_by === user?.email || isApprover(user));
  const canReview = isApprover(user) && order.status === 'pending_approval';

  const remove = async () => {
    if (!window.confirm(`Delete ${order.order_number}?`)) return;
    try { await axios.delete(`${API}/sales-orders/${order.order_id}`); toast.success('Deleted'); onClose(); onChanged(); }
    catch (e) { toast.error(apiError(e, 'Delete failed')); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="so-view-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle className="text-2xl font-mono">{order.order_number}</DialogTitle>
            <ApprovalBadge status={order.status} testId="so-view-status" />
          </div>
          <DialogDescription>{order.customer_name} · {order.service_type === 'ILL' ? 'Internet Leased Line' : 'MPLS'}</DialogDescription>
        </DialogHeader>

        {order.status === 'rejected' && order.rejection_reason && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="so-rejection-reason">
            <span className="font-semibold">Rejected:</span> {order.rejection_reason}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-2">
          <Row k="Bandwidth" v={`${order.bandwidth_mbps} Mbps`} />
          <Row k="IP Block" v={order.ip_block === 'none' ? 'Not required' : order.ip_block} />
          <Row k="Contract" v={`${order.contract_term_months} months`} />
          <Row k="Billing" v={order.billing_cycle?.replace('_', '-')} />
          <Row k="OTC" v={inr(order.otc)} />
          <Row k="MRC" v={inr(order.mrc)} />
          <Row k="Expected Delivery" v={order.expected_delivery_date} />
          <Row k="Raised By" v={order.created_by} />
        </div>

        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Sites</div>
          {order.sites.map((s, i) => (
            <div key={i} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`so-view-site-${i}`}>
              <span className="font-semibold mr-2">{s.label}</span>{s.address}{s.city ? `, ${s.city}` : ''}{s.pincode ? ` - ${s.pincode}` : ''}
              {(s.contact_person || s.contact_phone) && <div className="text-xs text-slate-500">{s.contact_person} {s.contact_phone}</div>}
            </div>
          ))}
        </div>

        {order.notes && <Row k="Notes" v={order.notes} />}

        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Attachments</div>
          {order.attachments?.length ? order.attachments.map(a => (
            <button key={a.attachment_id} type="button" onClick={() => download(API, order, a)} data-testid={`so-attachment-${a.attachment_id}`}
              className="flex items-center gap-2 text-sm text-sky-700 hover:underline">
              <Paperclip className="h-3.5 w-3.5" />{a.filename}<Download className="h-3.5 w-3.5" />
            </button>
          )) : <div className="text-sm text-slate-400">No attachments</div>}
        </div>

        {order.reviewed_by && <div className="text-xs text-slate-500">Reviewed by {order.reviewed_by} on {new Date(order.reviewed_at).toLocaleString()}</div>}

        <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-slate-100">
          {editable && <Button variant="ghost" className="text-red-600" onClick={remove} data-testid="so-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>}
          {editable && <Button variant="outline" onClick={() => onEdit(order)} data-testid="so-edit-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Button>}
          {editable && <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => onSubmit(order)} data-testid="so-submit-btn"><Send className="h-4 w-4 mr-1" />Submit for Approval</Button>}
          {canReview && <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => onReject(order)} data-testid="so-reject-btn"><X className="h-4 w-4 mr-1" />Reject</Button>}
          {canReview && <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onApprove(order)} data-testid="so-approve-btn"><Check className="h-4 w-4 mr-1" />Approve</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
